import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api, apiErrorMessage } from '../../services/api'
import { Button, ErrorText, Field, Input } from '../../components/ui'
import { Icon } from '../../components/ui/icons'
import { useAuthStore } from '../../context/auth'
import type { Appointment } from '../../types/api'

/**
 * Logística de la cita según su modalidad (H7): dirección de la visita a
 * domicilio o enlace de videollamada. Solo aparece cuando corresponde — una
 * consulta presencial no tiene ninguno de los dos.
 *
 * La dirección la gestiona recepción; el enlace lo puede fijar además el
 * profesional tratante, que es quien abre la sala.
 */
export function LogisticsPanel({ appointment }: { appointment: Appointment }) {
  const queryClient = useQueryClient()
  const user = useAuthStore((s) => s.user)
  const [visitAddress, setVisitAddress] = useState(appointment.visitAddress ?? '')
  const [visitNotes, setVisitNotes] = useState(appointment.visitNotes ?? '')
  const [videoCallUrl, setVideoCallUrl] = useState(appointment.videoCallUrl ?? '')
  const [error, setError] = useState('')
  const [saved, setSaved] = useState(false)

  const isHome = appointment.modality === 'HOME_VISIT'
  const isTele = appointment.modality === 'TELEMEDICINE'
  const canEditAddress = user?.role === 'ADMIN' || user?.role === 'RECEPTION'

  const save = useMutation({
    mutationFn: async () =>
      (
        await api.patch(`/appointments/${appointment.id}/logistics`, {
          ...(isHome && canEditAddress ? { visitAddress, visitNotes } : {}),
          ...(isTele ? { videoCallUrl } : {}),
        })
      ).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointment', appointment.id] })
      queryClient.invalidateQueries({ queryKey: ['appointments'] })
      queryClient.invalidateQueries({ queryKey: ['route'] })
      setError('')
      setSaved(true)
    },
    onError: (err) => {
      setSaved(false)
      setError(apiErrorMessage(err))
    },
  })

  // Una cita presencial no tiene logística que mostrar. El return va después
  // de los hooks para no romper el orden de llamadas de React.
  if (!isHome && !isTele) return null

  return (
    <div className="space-y-2 rounded-lg border border-brand-100 bg-soft p-3">
      <p className="flex items-center gap-1.5 text-sm font-semibold text-ink">
        <Icon name={isHome ? 'route' : 'video'} className="h-4 w-4 text-brand-600" />
        {isHome ? 'Visita a domicilio' : 'Telemedicina'}
      </p>

      {isHome &&
        (canEditAddress ? (
          <>
            <Field label="Dirección de la visita">
              <Input value={visitAddress} onChange={(e) => { setVisitAddress(e.target.value); setSaved(false) }} />
            </Field>
            <Field label="Referencias (opcional)">
              <Input value={visitNotes} onChange={(e) => { setVisitNotes(e.target.value); setSaved(false) }} />
            </Field>
          </>
        ) : (
          <div className="text-sm text-ink">
            <p>{appointment.visitAddress ?? 'Sin dirección registrada'}</p>
            {appointment.visitNotes && <p className="text-steel-600">{appointment.visitNotes}</p>}
          </div>
        ))}

      {isTele && (
        <>
          <Field label="Enlace de la videollamada" hint="El paciente lo ve en su portal.">
            <Input
              value={videoCallUrl}
              placeholder="https://…"
              onChange={(e) => { setVideoCallUrl(e.target.value); setSaved(false) }}
            />
          </Field>
          {appointment.videoCallUrl && (
            <a
              href={appointment.videoCallUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-2 text-sm font-semibold text-white hover:bg-brand-700"
            >
              <Icon name="video" className="h-4 w-4" />
              Abrir videollamada
            </a>
          )}
        </>
      )}

      {(isTele || canEditAddress) && (
        <div className="flex items-center gap-2">
          <Button variant="secondary" disabled={save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Guardando…' : 'Guardar'}
          </Button>
          {saved && <span className="text-xs text-emerald-700">Guardado</span>}
        </div>
      )}

      <ErrorText>{error}</ErrorText>
    </div>
  )
}
